import React, { useState, useContext, useEffect } from "react";
import flatten from "lodash.flatten";

import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import { Auth } from "api/makeApiRequest";
import { Params as SubmitEditableTableChangesParams } from "api/makeApiRequestType/makeSubmitEditableTableChanges";
import { DashboardContext } from "components/PageDashboard/PageDashboard";
import { useSessionTypes } from "hooks/useSessionTypes";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import { ParamsBase as UseMentorProductsParamsBase } from "hooks/useMentorProducts";
import { SearchResults } from "types-and-validators/SearchResults";
import { SingleSessionPrice } from "types-and-validators/SingleSessionPrice";
import { SessionType } from "types-and-validators/SessionType";
import { OnCompleteOutput } from "components/ModalWizard/ModalWizard";
import { useOnEditTable } from "components/PageDashboard/useOnEditTable";

type RowData = {
	item: SingleSessionPrice;
	sessionType: SessionType;
};

type ItemWithSessionType<T extends RowData> = T["item"] & T["sessionType"];

type OnEdit<T extends RowData> = (
	arg: SubmitEditableTableChangesParams<ItemWithSessionType<T>>["rows"]
) => OnCompleteOutput;

type UsePrices<T> = (
	arg: UseMentorProductsParamsBase
) => {
	isWaiting: boolean;
	isLoading: boolean;
	isError: boolean;
	output: SearchResults<T> | undefined;
};

type Props<T extends RowData> = {
	usePrices: UsePrices<T["item"]>;
	savePrices: (
		arg: SubmitEditableTableChangesParams<T["item"]>,
		auth: Auth
	) => ApiRequest<{}>;
	getRowData?: (arg: T["item"]) => Record<string, unknown>;
	table: ({
		onEdit,
		pricesWithSessionTypes,
		sessionTypesLoading,
		pricesLoading,
		sessionTypesWaiting,
		pricesWaiting,
		sessionTypesError,
		pricesError,
		waitModalTitle,
		waitModalIsShowing
	}: {
		onEdit: OnEdit<T>;
		pricesWithSessionTypes: ItemWithSessionType<T>[];
		sessionTypesLoading: boolean;
		pricesLoading: boolean;
		sessionTypesWaiting: boolean;
		pricesWaiting: boolean;
		sessionTypesError: boolean;
		pricesError: boolean;
		waitModalTitle: string;
		waitModalIsShowing: boolean;
	}) => React.ReactNode;
};

function PricesSection<T extends RowData>({
	usePrices,
	savePrices,
	getRowData,
	table
}: Props<T>) {
	const [{ isSignedIn, uid }] = useContext(AuthContext);

	const [dashboardContextState, setDashboardContextState] = useContext(
		DashboardContext
	);

	const { sessionTypesRefreshNum } = dashboardContextState;
	const {
		isWaiting: sessionTypesWaiting,
		isLoading: sessionTypesLoading,
		isError: sessionTypesError,
		output: sessionTypes
	} = useSessionTypes({
		mentorId: uid,
		showDisabled: true,
		doWait: isSignedIn === undefined || !uid,
		repeatNumber: sessionTypesRefreshNum,
		persistBetweenLoads: true
	});

	// We should refetch prices whever the session-types change, or the prices are edited.
	// So, set up a repeatNum for prices, which can be changed within this script, but also
	// update it whenever the sessionTypesRefreshNum changes.
	const [pricesRefreshNum, setPricesRefreshNum] = useState(
		sessionTypesRefreshNum
	);

	const {
		isWaiting: pricesWaiting,
		isLoading: pricesLoading,
		isError: pricesError,
		output: prices
	} = usePrices({
		mentorId: uid,
		showDisabled: true,
		doCurrencyConversion: false,
		doWait: isSignedIn === undefined || !uid,

		// NB! This depends on both pricesRefreshNum and sessionTypesRefreshNum so that this list
		// of session-prices updates whenever either type of object is edited.
		repeatNumber: pricesRefreshNum + sessionTypesRefreshNum,

		persistBetweenLoads: true,
		sessionTypeIds: sessionTypes
			? sessionTypes.page.map(sessionType => sessionType.id)
			: [],
		includePricesForGuestUid: "all"
	});

	const [waitModalIsShowing, setWaitModalIsShowing] = useState(false);
	const [waitModalTitle, setWaitModalTitle] = useState("");

	const havePrices =
		prices === undefined ? undefined : prices.page.length !== 0;

	useEffect(() => {
		setDashboardContextState(oldState => ({
			...oldState,
			havePrices
		}));
	}, [havePrices, setDashboardContextState]);

	const onEditPrices = useOnEditTable({
		setWaitModalTitle,
		setWaitModalIsShowing,
		onSaved: () => setPricesRefreshNum(oldState => oldState + 1),
		saveData: savePrices
	});

	// NB this should be done this way round (find prices for each sessionType,
	// rather than iterate through prices and add the appropriate sessionType)
	// because otherwise there is a risk of a problem if a price has been loaded,
	// but not its corresponding sessionType.
	const pricesWithSessionTypesNested = sessionTypes
		? sessionTypes.page.map(sessionType => {
				const { id: sessionTypeId } = sessionType;
				const pricesWithThisSessionType = (prices ? prices.page : []).filter(
					price => price.sessionTypeId === sessionTypeId
				);

				return pricesWithThisSessionType.map(product => {
					return {
						...product,
						...(getRowData ? getRowData(product) : {}),
						...sessionType
					};
				});
		  })
		: [];
	const pricesWithSessionTypes = sessionTypes
		? flatten(pricesWithSessionTypesNested)
		: [];

	// TODO:WV:20201009:Get the user started, maybe by asking for price during onboarding and / or adding a tutorial / walkthrough of how to set them up
	return (
		<>
			{table({
				onEdit: onEditPrices,
				pricesWithSessionTypes,
				sessionTypesLoading,
				pricesLoading,
				sessionTypesWaiting,
				pricesWaiting,
				sessionTypesError,
				pricesError,
				waitModalTitle,
				waitModalIsShowing
			})}
		</>
	);
}

export default PricesSection;
